import { CircleCheck, CircleX, Clock, LoaderCircle, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { JobPhase, SourceType, SyncProgress } from "@noa/api-core";
import { syncOutcome } from "~/utils/sync-progress";
import { Tag, type TagColor } from "~/components/ui";

const PHASE_CONFIG: Record<JobPhase, { color: TagColor; icon: React.ReactNode }> = {
  pending: { color: "default", icon: <RefreshCw size={14} className="animate-spin" /> },
  fetching: { color: "processing", icon: <LoaderCircle size={14} className="animate-spin" /> },
  fetched: { color: "cyan", icon: <CircleCheck size={14} /> },
  ingesting: { color: "processing", icon: <LoaderCircle size={14} className="animate-spin" /> },
  completed: { color: "success", icon: <CircleCheck size={14} /> },
  failed: { color: "error", icon: <CircleX size={14} /> },
};

type JobPhaseTagProps = {
  phase: JobPhase;
  sourceType: SourceType;
  syncProgress?: SyncProgress | null;
};

/** A job's phase, the same in its detail and in the history: a finished incremental sync
 * with files left reads "Partial", not "Completed". */
export function JobPhaseTag({ phase, sourceType, syncProgress }: JobPhaseTagProps) {
  const { t } = useTranslation("admin");

  if (syncOutcome(phase, syncProgress) === "partial") {
    return (
      <Tag color="warning" icon={<Clock size={14} />}>
        {t("uploadAdmin.progress.incremental.partialTag")}
      </Tag>
    );
  }

  // Sync sources discover their files instead of fetching them.
  const discovers = sourceType !== "blob";
  let label = t(`uploadAdmin.progress.phase.${phase}`);
  if (discovers && phase === "fetching") label = t("uploadAdmin.progress.phase.discovering");
  if (discovers && phase === "fetched") label = t("uploadAdmin.progress.phase.discovered");

  const { color, icon } = PHASE_CONFIG[phase];
  return (
    <Tag color={color} icon={icon}>
      {label}
    </Tag>
  );
}
